/**
 * Which interface languages this box offers — `CONTINUUM_LANGUAGES`, DECISIONS.md #56.
 *
 * The catalogues in the bundle are what *can* be offered (`catalogues.ts`); the box's configuration,
 * read from `GET /api/languages`, is what *is*. Fetched once, before anybody signs in, because the
 * selector is on the login screen.
 *
 * **Every bundled language until the answer arrives, and if it never does.** A box whose api is slow
 * to start, or a demo host behind a proxy that mangles the response, still has a working selector —
 * and the failure is a menu that offers too much, never one that offers nothing. Swedish is always
 * in the list, whatever the answer says: it is the reference and the fallback (`I18N.md` §1).
 *
 * Only the interface menus. A note in a language that is not offered still displays, and the note
 * editor's language choice is **not** narrowed by this at all (DECISIONS.md #57): what a note's text
 * is written in is record content, not a menu.
 */

import { useSyncExternalStore } from 'react'

import { languages, REFERENCE_LANGUAGE } from './catalogues'

let offered: readonly string[] = languages
const listeners = new Set<() => void>()

/** The bundled languages the configuration names, Swedish always among them, in the bundle's order. */
export function offeredFrom(enabled: readonly string[] | null): readonly string[] {
  if (enabled === null) {
    return languages
  }

  return languages.filter((language) => language === REFERENCE_LANGUAGE || enabled.includes(language))
}

/** The languages to offer right now. */
export function offeredLanguages(): readonly string[] {
  return offered
}

function publish(next: readonly string[]): void {
  offered = next
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Asks the box which languages it offers. Resolves with the list in force afterwards; never rejects.
 */
export async function loadOfferedLanguages(signal?: AbortSignal): Promise<readonly string[]> {
  try {
    const response = await fetch('/api/languages', {
      signal: signal ?? null,
      headers: { Accept: 'application/json' },
    })

    if (response.ok) {
      const body = (await response.json()) as { enabled?: unknown }
      const enabled = body.enabled

      if (enabled === null || (Array.isArray(enabled) && enabled.every((code) => typeof code === 'string'))) {
        publish(offeredFrom(enabled as readonly string[] | null))
      }
    }
  } catch {
    // Every bundled language, as before — see the file header.
  }

  return offered
}

/** For tests: back to every bundled language. */
export function resetOfferedLanguages(): void {
  publish(languages)
}

/** Module-level, so React does not unsubscribe and resubscribe on every render. */
function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The offered languages, re-rendering when the box's answer arrives. */
export function useOfferedLanguages(): readonly string[] {
  return useSyncExternalStore(subscribe, offeredLanguages, offeredLanguages)
}
